import { removeLegacyFilters, unlockTenantFilters } from "../components/helpers/filtersTenantStorage";
import AppStore from "./AppStore";
import ChatCategoryStore from "./ChatCategoryStore";
import ChatStore from "./ChatStore";
import ClaimsStore from "./ClaimsStore";
import ObservabilityStore from "./ObservabilityStore";
import ReleaseNotesStore from "./ReleaseNotesStore";
import TicketStore from "./TicketStore";
import UploadHistoryStore from "./UploadHistoryStore";

/**
 * Root store that owns AppStore, ChatCategoryStore, ChatStore, ClaimsStore and others in the future.
 * Sub-stores get a reference to MainStore so they can access other stores
 * via this.mainStore.appStore, this.mainStore.chatStore, etc.
 * This way we can avoid filling functions with properties and local refs
 */
class MainStore {
  readonly appStore: AppStore;
  readonly chatCategoryStore: ChatCategoryStore;
  readonly chatStore: ChatStore;
  readonly claimsStore: ClaimsStore;
  readonly observabilityStore: ObservabilityStore;
  readonly releaseNotesStore: ReleaseNotesStore;
  readonly ticketStore: TicketStore;
  readonly uploadHistoryStore: UploadHistoryStore;

  // "<tenantId>:<userId>" of the session, set by AuthContext; null until both are known.
  // Saved filters in localStorage are keyed by it (see filtersTenantStorage).
  filtersScope: string | null = null;
  // The session's filters encryption key (base64) and its id that filtersScope was unlocked with.
  private filtersKey: string | null = null;
  private filtersKeyId: string | null = null;
  // The latest setFiltersScope's unlock + restore; a call that changes nothing awaits it too.
  private filtersReady: Promise<void> = Promise.resolve();

  constructor() {
    this.appStore = new AppStore(this);
    this.chatCategoryStore = new ChatCategoryStore(this);
    this.chatStore = new ChatStore(this);
    this.claimsStore = new ClaimsStore(this);
    this.observabilityStore = new ObservabilityStore(this);
    this.releaseNotesStore = new ReleaseNotesStore(this);
    this.ticketStore = new TicketStore(this);
    this.uploadHistoryStore = new UploadHistoryStore(this);
  }

  // Stores restore their saved filters when created, before the session (and so the
  // tenant and user) is known; once the session's key has unlocked (decrypted) that scope's
  // filters, re-restore them. The research pages restore theirs on mount (ChatCategoryStore),
  // which happens after this. Await it before rendering pages: until it resolves the saved
  // filters read as empty and changes to them are not saved.
  // Assumes the scope is set once per page load: a tenant change always reloads the page
  // (ChangeTenantDialog navigates with window.location.assign), as does signing in as
  // another user. filtersScope is not observable and ChatCategoryStore is not re-restored
  // here, so a mid-session change would leave a mounted research page and anything that
  // read filtersScope during render on the previous scope.
  // Resolves once the latest scope is ready, also when this call changed nothing (the same
  // scope while its unlock is still in flight) or was superseded by a newer one.
  setFiltersScope(
    tenantId: string | null | undefined,
    userId: string | null | undefined,
    encryptionKey?: string | null,
    keyId?: string | null,
  ): Promise<void> {
    const next = tenantId && userId ? `${tenantId}:${userId}` : null;
    const key = next ? encryptionKey ?? null : null;
    const kid = next ? keyId ?? null : null;
    if (next === this.filtersScope && key === this.filtersKey && kid === this.filtersKeyId) return this.filtersReady;
    this.filtersScope = next;
    this.filtersKey = key;
    this.filtersKeyId = kid;
    if (next) removeLegacyFilters();
    const ready: Promise<void> = unlockTenantFilters(next, key, kid).then(() => {
      // A newer call took over while decrypting; it restores for its own scope.
      if (this.filtersReady !== ready) return this.filtersReady;
      this.claimsStore.restorePersistedFilters();
      this.observabilityStore.restorePersistedFilters();
      this.uploadHistoryStore.restorePersistedFilters();
    });
    this.filtersReady = ready;
    return ready;
  }
}

const mainStore = new MainStore();

export default MainStore;
export { mainStore };
